import { CSSProperties, useState } from 'react'
import { colorPrimario } from '../../styles/colors'
 
interface DownloadFileProps {
	fileName: string
	base64Content: string
	mimeType?: string
}
 
export const DownloadFile = ({ fileName, base64Content, mimeType = 'application/octet-stream' }: DownloadFileProps) => {
	const [isHover, setIsHover] = useState(false)
	// Validación inicial de props
	if (!base64Content) {
		return (
			<div style={{
				padding: '8px 16px',
				backgroundColor: '#ffebee',
				color: '#c62828',
				border: '1px solid #ffcdd2',
				borderRadius: '6px',
				fontFamily: 'Raleway',
				fontSize: '14px'
			}}>
				⚠️ Error: No se proporcionó contenido Base64 para descargar
			</div>
		)
	}
 
	const cleanBase64 = (base64: string | undefined | null): string => {
		// Validar que el contenido no sea undefined, null o vacío
		if (!base64 || typeof base64 !== 'string') {
			throw new Error('El contenido Base64 es requerido y debe ser una cadena de texto')
		}
		 
		// Remover prefijos comunes como "data:image/png;base64," o "data:application/pdf;base64,"
		const base64Data = base64.replace(/^data:[^;]+;base64,/, '')
		 
		// Remover espacios en blanco y saltos de línea
		return base64Data.replace(/\s/g, '')
	}
 
	const isValidBase64 = (str: string): boolean => {
		try {
			// Verificar que la cadena solo contenga caracteres válidos de Base64
			const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
			if (!base64Regex.test(str)) {
				return false
			}
			 
			// Intentar decodificar para verificar que sea válido
			atob(str)
			return true
		} catch {
			return false
		}
	}
 
	const handleDownload = () => {
		try {
			// Limpiar y validar el contenido Base64
			const cleanBase64Content = cleanBase64(base64Content)
			 
			if (!cleanBase64Content) {
				throw new Error('El contenido Base64 está vacío')
			}
			 
			if (!isValidBase64(cleanBase64Content)) {
				throw new Error('El contenido no es un Base64 válido')
			}
 
			// Convertir Base64 a Blob usando una función más robusta
			const binaryString = atob(cleanBase64Content)
			const bytes = new Uint8Array(binaryString.length)
			 
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i)
			}
			 
			const blob = new Blob([bytes], { type: mimeType })
			 
			// Verificar que el blob no esté vacío
			if (blob.size === 0) {
				throw new Error('El archivo generado está vacío')
			}
			 
			// Crear URL temporal para el blob
			const url = window.URL.createObjectURL(blob)
			 
			// Crear elemento de descarga temporal
			const link = document.createElement('a')
			link.href = url
			link.download = fileName
			link.style.display = 'none'
			document.body.appendChild(link)
			link.click()
			 
			// Limpiar
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
			 
			console.log(`Archivo ${fileName} descargado exitosamente`)
		} catch (error) {
			console.error('Error al descargar el archivo:', error)
			const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
			alert(`Error al descargar el archivo: ${errorMessage}`)
		}
	}
 
	const linkStyles: CSSProperties = {
		display: 'inline-flex',
		alignItems: 'center',
		padding: '8px 16px',
		color: isHover ? 'white' : colorPrimario,
		backgroundColor: isHover ? colorPrimario : 'transparent',
		border: `2px solid ${colorPrimario}`,
		borderRadius: '6px',
		textDecoration: 'none',
		fontFamily: 'Raleway',
		fontWeight: '600',
		fontSize: '14px',
		cursor: 'pointer',
		transition: 'all 0.2s ease-in-out',
		gap: '8px',
		margin: '0 0 24px 0'
	}
 
	const iconStyles: CSSProperties = {
		fontSize: '16px'
	}
 
	return (
		<a
			style={linkStyles}
			onMouseOver={() => setIsHover(true)}
			onMouseLeave={() => setIsHover(false)}
			onClick={(e) => {
				e.preventDefault()
				handleDownload()
			}}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					handleDownload()
				}
			}}
		>
			<span style={iconStyles}>📥</span>
			<span>Descargar {fileName}</span>
		</a>
	)
}
